import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import axios from "../../../lib/http/axiosConfig";
import BatteryIcon from "../../common/BatteryIcon";
import "./Information.css";
import { useDispatch, useSelector } from "react-redux";
import { Row } from "../../common/Row";
import { StatusDot } from "../../common/StatusDot";
import EnergyDeviceCard from "./EnergyDeviceCard";
import { useDemoScenario } from "../../../demo/DemoScenarioContext";
import { setEnergyDevices } from "../../../features/FarmSlice";
import { normalizeInfoEnergyDevices } from "../../../utils/energyDevices";
import { gpsQuality, GPS_NEEDS_CHECK } from "../../../utils/gpsStatus";
import { useLang, useT } from "../../../i18n";

export default function Information() {
  const t = useT();
  const [lang] = useLang();
  const dispatch = useDispatch();
  const farmId = useSelector((state) => state.farm.farmId);
  const controls = useSelector((state) => state.drone.control);
  const energyDeviceFocus = useSelector((state) => state.farm.energyDeviceFocus);
  const { isDemo, demoInfo } = useDemoScenario();

  const [farm, setFarm] = useState("");
  const [drone, setDrone] = useState("");
  // 에너지 장치(스마트폴 · 태양광큐브) 목록 { mode, devices }. 응답 전엔 null → 카드를 그리지 않는다.
  // 옛 응답(poleInfo 1개)은 normalizeInfoEnergyDevices 가 장치 1대 목록으로 바꿔 준다.
  const [energyDevices, setEnergyDevicesState] = useState(null);

  // 장비 카드 접기/펼치기 (기본: 접힘 → 요약만 표시). 큐브는 여러 대라 줄마다 따로 연다.
  const [openIds, setOpenIds] = useState(() => new Set());
  const [droneOpen, setDroneOpen] = useState(false);

  const normalizeBatteryPercent = (value, fallback = null) => {
    if (value === null || value === undefined || value === "") return fallback;
    const num = Number(value);
    if (!Number.isFinite(num)) return fallback;
    return Math.max(0, Math.min(100, Math.round(num)));
  };

  // 지도 마커를 누르면 그 장치 줄을 펼친다. 같은 장치를 다시 눌러도(at 이 바뀜) 다시 연다.
  useEffect(() => {
    if (energyDeviceFocus?.id == null) return;
    setOpenIds((prev) => {
      const next = new Set(prev);
      next.add(energyDeviceFocus.id);
      return next;
    });
  }, [energyDeviceFocus]);

  // 지도(EnergyDeviceMarkers)가 좌표를 읽을 수 있게 redux 에도 둔다. 지도가 따로 부르지 않아 호출이 늘지 않는다.
  useEffect(() => {
    dispatch(setEnergyDevices(energyDevices));
  }, [dispatch, energyDevices]);

  // 농장이 바뀌면 이전 농장 장치가 새 응답이 올 때까지 지도에 남지 않게 비운다.
  useEffect(() => {
    setEnergyDevicesState(null);
    setOpenIds(new Set());
  }, [farmId]);

  // 데모는 농장 정보를 한 번만 받는다. 아래 fetchInfoData 주석 참고.
  const demoFarmLoadedRef = useRef(false);

  // 농장, 드론 정보 가져오기 (5초 주기 폴링)
  useEffect(() => {
    if (!farmId) return;

    let cancelled = false;

    // 데모의 드론·에너지 장치는 DB 에 없는 가짜 장비(id 99001 / DEMO-Cx)라 상수를 쓴다.
    // 농장은 실재하므로 실계정과 같은 응답을 쓴다 — 지도 좌하단(FarmOverlay)이 이미
    // 같은 엔드포인트를 보고 있어서, 여기만 상수로 두면 왼쪽은 "양배추재배장",
    // 오른쪽은 "농기원" 으로 한 화면에서 두 이름이 나온다.
    const applyDemoDevices = () => {
      setEnergyDevicesState(demoInfo.energyDevices);
      // 등록 드론이 없으면(demoInfo.drone === null) 실계정처럼 드론 카드를 비운다.
      setDrone(
        demoInfo.drone
          ? {
              ...demoInfo.drone,
              powerStatus: controls.powerStatus,
              armingStatus: controls.armingStatus,
              missionStatus: controls.missionStatus,
            }
          : null
      );
    };

    const fetchInfoData = async () => {
      // 데모는 서버에 상태가 없다. 다시 물어도 같은 답이고, 이 호출이 토큰 만료 순간에
      // 401 을 맞으면 재발급 → localStorage 의 accessToken 교체 → DemoScenarioContext 의
      // 프로필 effect 재실행 → 앱 전체 리마운트(로딩 화면)로 이어진다.
      // 그 리마운트가 데모 드론을 전원 OFF 로 되돌려 "비행 불가" 를 만든다.
      if (isDemo && demoFarmLoadedRef.current) {
        applyDemoDevices();
        return;
      }
      try {
        const { data } = await axios.get(`/api/dashboard/${farmId}/info`);
        if (cancelled) return;
        const info = data.data;

        setFarm(info.farmInfo);
        if (isDemo) {
          demoFarmLoadedRef.current = true;
          applyDemoDevices();
          return;
        }
        setDrone(info.droneInfo);
        setEnergyDevicesState(normalizeInfoEnergyDevices(info));
      } catch (error) {
        if (cancelled) return;
        console.error("Infomation 탭 정보 불러오기 실패: ", error);
        // 농장 조회가 실패해도 데모 장비 카드는 비면 안 된다.
        if (isDemo) applyDemoDevices();
      }
    };

    fetchInfoData();
    // 데모는 폴링하지 않는다. 장비 카드는 조작 상태(controls)나 데모 구성(demoInfo)이 바뀔 때
    // 이 effect 가 다시 돌면서 applyDemoDevices 로 갱신되므로 주기 조회가 필요 없다.
    const interval = isDemo ? null : setInterval(fetchInfoData, 5000);

    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
    };
  }, [controls, demoInfo, farmId, isDemo]);

  const toggleDevice = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const droneDisconnected = drone && drone.isConnected === false;

  const droneBatteryPercent = drone
    ? normalizeBatteryPercent(drone.batteryCharge, null)
    : null;

  // Offline 시 default 강제: 파워 OFF / GPS Poor / 배터리 -.
  const dronePowered = drone && !droneDisconnected && drone.powerStatus === true;
  const dronePowerText = dronePowered
    ? t("information.drone.power.on")
    : t("information.drone.power.off");
  const droneBatteryText =
    dronePowered && droneBatteryPercent != null ? `${droneBatteryPercent}%` : "-";
  // 측위 단계(1~8)를 그대로 등급으로 옮긴다. 전원이 켜졌다고 Good 으로 보면
  // 측위가 안 잡힌 드론도 정상으로 보여 미션 시작이 막히는 이유를 알 수 없다.
  const droneGpsLevel = dronePowered ? drone?.gpsStatus : null;
  const droneGpsQuality = gpsQuality(droneGpsLevel);
  // gpsQuality 는 비교용 값("Good"/"Poor"/"Unknown")을 돌려주므로 표시 문구는 여기서 옮긴다.
  const droneGpsText = !dronePowered
    ? droneDisconnected
      ? t("information.gps.quality.Poor")
      : "-"
    : droneGpsQuality == null
      ? "-"
      : t("information.gps.qualityWithLevel", {
          quality: t(`information.gps.quality.${droneGpsQuality}`),
          level: droneGpsLevel,
        });
  const droneGpsGood = droneGpsQuality === "Good";

  // 5(3D fix)는 비행은 되지만 측위가 불안정할 수 있어 확인을 받는다.
  // 5초 폴링이라 값이 5로 바뀌는 순간에만 한 번 띄우고, 5를 벗어났다 돌아오면 다시 띄운다.
  const prevGpsLevelRef = useRef(null);
  useEffect(() => {
    const prev = prevGpsLevelRef.current;
    prevGpsLevelRef.current = droneGpsLevel;
    if (droneGpsLevel === GPS_NEEDS_CHECK && prev !== GPS_NEEDS_CHECK) {
      toast.warn(t("information.gps.needsCheck"), {
        toastId: "info-gps-needs-check",
      });
    }
  }, [droneGpsLevel, t]);

  const droneSummary = droneDisconnected
    ? t("information.summary.disconnected")
    : !dronePowered
      ? t("information.summary.powerOff")
      : [
          droneBatteryPercent == null
            ? null
            : t("information.summary.battery", { percent: droneBatteryPercent }),
          t("information.summary.signal", {
            status: droneGpsGood ? t("information.summary.signalGood") : droneGpsText,
          }),
        ]
          .filter(Boolean)
          .join(" · ");

  return (
    <>
      {/* Farm */}
      {farm && (
        <section className="sb-card">
          <h3 className="sb-card-title">{t("information.farm.title")}</h3>
          {/* 농장명 크게 (실데이터: farmInfo.name / farmInfo.location) */}
          <div className="calm-farmhead">
            <div className="nm">{farm.name}</div>
            <div className="ds">{farm.location}</div>
          </div>
          {/* 농장명과 위치는 상단 헤더에 이미 표시되므로 행에서는 생략 */}
          {/* 작물명은 서버 값. 영어 모드면 cropNameEn, 없으면(데모·옛 응답) cropName 그대로. */}
          <Row label={t("information.farm.cropName")}>
            {(lang === "en" && farm.cropNameEn) || farm.cropName}
          </Row>
          <Row label={t("information.farm.size")}>
            {farm.farmSize == null ? "-" : `${farm.farmSize} m²`}
          </Row>
          {/* 조회 탭과 지도가 계속 "N칸"으로 말하는데 그 N 이 어디서 온
              숫자인지 화면 어디에도 없었다. 기준을 여기 한 줄로 둔다. */}
          <Row label={t("information.farm.totalCells")}>
            {farm.cellCount == null
              ? "-"
              : t("information.farm.cellsValue", { count: farm.cellCount })}
          </Row>
        </section>
      )}

      {/* 에너지 장치: 스마트폴 1대 또는 태양광큐브 여러 대 */}
      {energyDevices && (
        <EnergyDeviceCard
          energyDevices={energyDevices}
          openIds={openIds}
          onToggle={toggleDevice}
        />
      )}

      {/* Drone */}
      {drone && (
        <section className={`sb-card${droneDisconnected ? " sb-card--offline" : ""}`}>
          <h3 className="sb-card-title">
            {t("information.drone.title")}
            {droneDisconnected && (
              <span className="sb-offline-badge">{t("information.drone.offline")}</span>
            )}
          </h3>

          <button
            type="button"
            className={`calm-dev${droneDisconnected ? " off" : ""}${droneOpen ? " open" : ""}`}
            onClick={() => setDroneOpen((v) => !v)}
            aria-expanded={droneOpen}
          >
            <span className="ic">
              <svg viewBox="0 0 24 24">
                <path d="M5 5l4 4M19 5l-4 4M5 19l4-4M19 19l-4-4" />
                <rect x="9" y="9" width="6" height="6" rx="2" />
              </svg>
            </span>
            <span className="info">
              <span className="n">{drone.droneName}</span>
              <span className="s">{droneSummary}</span>
            </span>
            <span className="arw" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </span>
          </button>

          {droneOpen && (
          <div className="calm-dev-body">
          {/* 오프라인/전원 OFF 상태에서는 실시간 telemetry 값을 표시하지 않는다. */}
          <Row label={t("information.drone.powerStatus")}>
            {dronePowerText}
          </Row>
          <Row label={t("information.drone.battery")}>
            <span className="percent">
              {droneBatteryText}
              {dronePowered && droneBatteryPercent != null && (
                <BatteryIcon level={droneBatteryPercent} />
              )}
            </span>
          </Row>
          <Row label={t("information.drone.gpsSignalStatus")}>
            {droneGpsText}
            {dronePowered && droneGpsQuality != null && (
              <StatusDot text={droneGpsGood} />
            )}
            {droneDisconnected && <StatusDot text={false} />}
          </Row>
          </div>
          )}
        </section>
      )}
    </>
  );
}
